// Copyright (c) 2019-26 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';

import formatDate from '../../../utilities/formatDate';

import '../../../styles/planeditor.css';

// Detail pane for one pending CX plan in the Plan Editor's CX Review mode.
// Actions route through props.resolveCx (which refetches pending + plans).
export default class CxReviewPane extends React.Component {

    state = {
        // Create-instead form. CX's plan code arrives as payload.businessKey
        // and is the natural internalPlanId for the new TA plan — pre-fill it,
        // reviewer can still override.
        internalPlanId: this.props.row?.payload?.businessKey ?? "",
        directory: "",
        saving: false
    }

    componentDidUpdate = (prev) => {
        // Reset the create form when the selected pending row changes
        if (prev.row?._id !== this.props.row?._id)
            this.setState({
                internalPlanId: this.props.row?.payload?.businessKey ?? "",
                directory: "",
                saving: false
            });
    }

    act = async (body, successMsg) => {
        if (this.state.saving) return;
        this.setState({ saving: true });
        const res = await this.props.resolveCx(body);
        this.setState({ saving: false });
        if (res?.success && successMsg)
            toast.success(successMsg);
        // Failure: api.js has already toasted the server error (e.g. duplicate
        // internalPlanId on create) — keep the form contents for correction.
    }

    createInstead = async () => {
        const id = this.state.internalPlanId.trim();
        if (!id)
            return toast.error("Internal Plan ID is required to create a plan.");
        await this.act({
            action: "create",
            pendingId: this.props.row._id,
            internalPlanId: id,
            directory: this.state.directory.trim()
        }, "Plan created and linked to CX.");
    }

    render = () => {
        const row = this.props.row;
        if (!row)
            return <div className="plan-editor-page" />;

        const pl = row.payload ?? {};
        const sug = row.suggestion;

        return (<div className="plan-editor-page">
            <div className="plan-page-header">
                <h2>{pl.name || "Unnamed plan"}</h2>
                <span className="cx-badge-pending">Pending CX review</span>
            </div>

            {/* CX payload fields */}
            <div className="cx-review-fields">
                <div className="cx-review-field">
                    <h3>Plan Type</h3>
                    <p>{pl.type || "—"}</p>
                </div>
                <div className="cx-review-field">
                    <h3>Plan Year End</h3>
                    <p>{pl.pyeMonth && pl.pyeDay ? pl.pyeMonth + "/" + pl.pyeDay : "—"}</p>
                </div>
                <div className="cx-review-field">
                    <h3>Effective Date</h3>
                    <p>{pl.effectiveDate ? formatDate(pl.effectiveDate, false, true) : "—"}</p>
                </div>
                <div className="cx-review-field">
                    <h3>Client (CX)</h3>
                    <p>{pl.clientName || "—"}</p>
                </div>
                <div className="cx-review-field">
                    <h3>Status (CX)</h3>
                    <p>{pl.active === false ? "Inactive" : "Active"}</p>
                </div>
                <div className="cx-review-field">
                    <h3>Received</h3>
                    <p>{row.receivedAt ? formatDate(row.receivedAt) : "—"}</p>
                </div>
            </div>

            {/* Suggested match (computed at read time by the API) */}
            {sug ? <div className="cx-suggestion">
                <span className="cx-suggestion-chip">
                    Suggested match: {sug.internalPlanId} · {sug.planName}{sug.exact ? " (exact ID match)" : ""}
                </span>
                <div
                    className="cx-link-button"
                    onClick={() => this.act({
                        action: "link",
                        pendingId: row._id,
                        planId: sug.planId
                    }, "Linked to " + sug.internalPlanId + ".")}
                >Link to {sug.internalPlanId}</div>
            </div> : <p style={{ padding: 5, color: "var(--color-text-light)" }}>
                No suggested match found among your unlinked plans.
            </p>}

            <div
                className="cx-dismiss-link"
                onClick={() => this.act({
                    action: "dismiss",
                    pendingId: row._id
                }, "Dismissed. A future edit in CX will re-queue it.")}
            >Dismiss</div>

            {/* Create a new TA plan from this CX payload */}
            <div className="cx-create-form">
                <h3>Or create a new plan from this CX record</h3>
                <input
                    type="text"
                    value={this.state.internalPlanId}
                    onChange={e => this.setState({ internalPlanId: e.target.value })}
                    placeholder="Internal Plan ID (required)..."
                />
                <input
                    type="text"
                    value={this.state.directory}
                    onChange={e => this.setState({ directory: e.target.value })}
                    placeholder="Plan Directory (optional)..."
                />
                <div
                    className="cx-create-button"
                    style={this.state.saving ? { opacity: 0.5, cursor: 'wait' } : {}}
                    onClick={this.createInstead}
                >Create Plan Instead</div>
            </div>
        </div>);
    }
}
